import React, { useState, useEffect } from 'react';
import { useNotifications } from '../../contexts/NotificationContext';
import type { NotificationFilters } from '../../utils/notificationService';

const NotificationsPage: React.FC = () => {
  const { state, fetchNotifications, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const [filters, setFilters] = useState<NotificationFilters>({
    page: 1,
    per_page: 20,
    status: 'all',
    type: 'all'
  });
  const [selectedNotifications, setSelectedNotifications] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchNotifications(filters);
  }, [filters]);

  const handleFilterChange = (newFilters: Partial<NotificationFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    handleFilterChange({ search: term });
  };

  const handleSelectNotification = (notificationId: number) => {
    setSelectedNotifications(prev => 
      prev.includes(notificationId)
        ? prev.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedNotifications.length === state.notifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(state.notifications.map(n => n.id));
    }
  };

  const handleMarkSelectedAsRead = async () => {
    if (selectedNotifications.length > 0) {
      await markAsRead(selectedNotifications);
      setSelectedNotifications([]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedNotifications.length > 0) {
      for (const id of selectedNotifications) {
        await deleteNotification(id);
      }
      setSelectedNotifications([]);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNotificationIcon = (category: string) => {
    switch (category) {
      case 'provider':
        return 'bi-building';
      case 'parent':
        return 'bi-person';
      case 'system':
        return 'bi-gear';
      default:
        return 'bi-bell';
    }
  };

  const getNotificationColor = (category: string) => {
    switch (category) {
      case 'provider':
        return 'text-primary';
      case 'parent':
        return 'text-success';
      case 'system':
        return 'text-warning';
      default:
        return 'text-secondary';
    }
  };

  return (
    <div className="container mt-5 pt-5">
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Notifications</h2>
            <div className="d-flex gap-2">
              {selectedNotifications.length > 0 && (
                <>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleMarkSelectedAsRead}
                  >
                    Mark as Read ({selectedNotifications.length})
                  </button>
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={handleDeleteSelected}
                  >
                    Delete ({selectedNotifications.length})
                  </button>
                </>
              )}
              {state.unreadCount > 0 && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={markAllAsRead}
                >
                  Mark All as Read
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search notifications..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange({ status: e.target.value as any })}
                  >
                    <option value="all">All Status</option>
                    <option value="unread">Unread</option>
                    <option value="read">Read</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={filters.type}
                    onChange={(e) => handleFilterChange({ type: e.target.value as any })}
                  >
                    <option value="all">All Types</option>
                    <option value="provider">Provider</option>
                    <option value="parent">Parent</option>
                    <option value="system">System</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={filters.per_page}
                    onChange={(e) => handleFilterChange({ per_page: parseInt(e.target.value) })}
                  >
                    <option value={10}>10 per page</option>
                    <option value={20}>20 per page</option>
                    <option value={50}>50 per page</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Notifications List */}
          <div className="card">
            <div className="card-body p-0">
              {state.loading ? (
                <div className="text-center p-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : state.notifications.length === 0 ? (
                <div className="text-center p-5">
                  <i className="bi bi-bell-slash fs-1 text-muted"></i>
                  <h5 className="mt-3">No notifications found</h5>
                  <p className="text-muted">You're all caught up!</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedNotifications.length === state.notifications.length}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th>Type</th>
                        <th>Title</th>
                        <th>Message</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.notifications.map((notification) => (
                        <tr key={notification.id} className={!notification.is_read ? 'table-warning' : ''}>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedNotifications.includes(notification.id)}
                              onChange={() => handleSelectNotification(notification.id)}
                            />
                          </td>
                          <td>
                            <i className={`bi ${getNotificationIcon(notification.notification_type.category)} ${getNotificationColor(notification.notification_type.category)}`}></i>
                            <span className="ms-2 small text-muted">
                              {notification.notification_type.category}
                            </span>
                          </td>
                          <td>
                            <strong>{notification.title}</strong>
                          </td>
                          <td>
                            <span className="text-muted">
                              {notification.message.length > 60 
                                ? `${notification.message.substring(0, 60)}...` 
                                : notification.message
                              }
                            </span>
                          </td>
                          <td>
                            <small className="text-muted">
                              {formatDate(notification.created_at)}
                            </small>
                          </td>
                          <td>
                            {notification.is_read ? (
                              <span className="badge bg-secondary">Read</span>
                            ) : (
                              <span className="badge bg-primary">Unread</span>
                            )}
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              {!notification.is_read && (
                                <button
                                  className="btn btn-outline-primary"
                                  onClick={() => markAsRead([notification.id])}
                                  title="Mark as read"
                                >
                                  <i className="bi bi-check"></i>
                                </button>
                              )}
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => deleteNotification(notification.id)}
                                title="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Pagination */}
          {state.pagination && state.pagination.pages > 1 && (
            <nav className="mt-4">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${filters.page === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handleFilterChange({ page: filters.page! - 1 })}
                    disabled={filters.page === 1}
                  >
                    Previous
                  </button>
                </li>
                
                {Array.from({ length: Math.min(5, state.pagination.pages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <li key={page} className={`page-item ${filters.page === page ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handleFilterChange({ page })}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}
                
                <li className={`page-item ${filters.page === state.pagination.pages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => handleFilterChange({ page: filters.page! + 1 })}
                    disabled={filters.page === state.pagination.pages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}

          {/* Stats */}
          {state.stats && (
            <div className="row mt-4">
              <div className="col-md-3">
                <div className="card text-center">
                  <div className="card-body">
                    <h5 className="card-title">{state.stats.total}</h5>
                    <p className="card-text text-muted">Total</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center">
                  <div className="card-body">
                    <h5 className="card-title text-primary">{state.stats.unread}</h5>
                    <p className="card-text text-muted">Unread</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center">
                  <div className="card-body">
                    <h5 className="card-title text-success">{state.stats.read}</h5>
                    <p className="card-text text-muted">Read</p>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card text-center">
                  <div className="card-body">
                    <h5 className="card-title text-warning">
                      {Object.values(state.stats.by_category).reduce((a, b) => a + b, 0)}
                    </h5>
                    <p className="card-text text-muted">This Month</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage; 